import { useCallback, useRef, useState } from 'react';

import { z } from 'zod';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';

import { getErrorMessage, schema } from './schema';
import { validateAskSchema } from './utils';

const MAX_IMAGE_GENERATION_COUNT = 4;

type JobStatus = 'idle' | 'pending' | 'success' | 'error';
type FormErrors = { ask: string | null; description: string | null };

type UseCreateReturn = {
	data: {
		ask: string;
		description: string;
		mode: string | undefined | null;
		jobStatus: JobStatus;
		imageSrc: string | undefined;
		imageRegenerationLeft: number;
		imageRegenerationCount: number;
		imageRegenerationDisabled: boolean;
		imageButtonLabel: TranslationKey;
		imageButtonIcon: 'loader' | 'ai';
		errors: FormErrors;
		formRef: React.RefObject<HTMLFormElement | null>;
	};
	actions: {
		back: () => void;
		submit: () => void;
		toOptionalPreview: () => void;
		uploadImage: () => void;
		regenerateAiImage: () => void;
		resetFieldError: (field: 'ask' | 'description') => void;
		persistDescription: (editorContent: string) => void;
		persistAsk: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
	};
};

// oxlint-disable max-statements, complexity, max-lines-per-function
export function useCreate(): UseCreateReturn {
	const { typedTranslate } = useI18n();
	const {
		data: { draft, normalizedStep },
		actions,
	} = useStartAPetitionPageState();
	const track = useTracking();

	const mode = draft?.metadata?.draftMode;

	// petition form actions
	const formRef = useRef<HTMLFormElement>(null);
	const [formErrors, setFormErrors] = useState<FormErrors>({ ask: null, description: null });

	const submit = useCallback(
		(target: 'location' | 'optional-preview') => {
			const formData = new FormData(formRef.current ?? undefined);
			const formDataObject: Record<string, string> = {};
			formData.forEach((value, key) => {
				// oxlint-disable-next-line @typescript-eslint/no-base-to-string
				formDataObject[key] = value.toString();
			});
			const result = schema.safeParse(formDataObject);

			if (!result.success) {
				const { fieldErrors } = z.flattenError(result.error);
				const askError = getErrorMessage(fieldErrors.ask?.[0], typedTranslate);
				const descriptionError = getErrorMessage(fieldErrors.description?.[0], typedTranslate);
				setFormErrors({ ask: askError, description: descriptionError });
				return;
			}

			const petitionDraft = { ask: result.data.ask, description: result.data.description };
			actions.send({ type: 'submit', draft: { petitionDraft, editingDraft: null }, target });
		},
		[actions, typedTranslate],
	);

	const resetFieldError = useCallback((field: 'ask' | 'description') => {
		setFormErrors((prev) => ({ ...prev, [field]: null }));
	}, []);

	const validateField = useCallback(
		(field: 'ask' | 'description', value: string): string | null => {
			const result = schema.shape[field].safeParse(value);
			if (result.success) return null;
			return getErrorMessage(result.error.issues[0]?.message, typedTranslate);
		},
		[typedTranslate],
	);

	// image actions
	const { mutate, status } = useImageJob({ enabled: true });
	const imageSrc = draft?.petitionDraft?.image?.url;
	const imageGenerationCount = draft?.metadata?.imageGenerationCount ?? 0;

	const currentAsk = draft?.editingDraft?.ask ?? draft?.petitionDraft?.ask ?? '';
	const currentDescription = draft?.editingDraft?.description ?? draft?.petitionDraft?.description ?? '';

	const isAskValid = validateAskSchema(currentAsk);
	const imageRegenerationDisabled = imageGenerationCount >= MAX_IMAGE_GENERATION_COUNT || !isAskValid;
	const uploadImage = useCallback(() => {
		void track('sap_add_own_image_click');

		actions.send({ type: 'submit', target: 'media' });
	}, [actions, track]);

	const regenerateAiImage = useCallback(() => {
		if (status === 'pending' || imageRegenerationDisabled) return;

		void track('sap_generate_ai_image_click', { regenerations: imageGenerationCount });

		const prompt = `${currentAsk} ${currentDescription}`;

		// For regeneration, we want to use the combined prompt of ask and description and not pass personalStory
		const input = {
			mainPrompt: prompt,
		};
		mutate(input);
	}, [currentAsk, currentDescription, imageGenerationCount, imageRegenerationDisabled, mutate, status, track]);

	const persistDescription = useCallback(
		(editorContent: string) => {
			const editingDraft = { description: editorContent };
			actions.send({ type: 'patch', draft: { editingDraft } });
			setFormErrors((prev) => ({ ...prev, description: validateField('description', editorContent) }));
		},
		[actions, validateField],
	);

	const persistAsk = useCallback(
		(event: React.FocusEvent<HTMLTextAreaElement>) => {
			const askValue = event.target.value.trim();
			const editingDraft = { ask: askValue };
			actions.send({ type: 'patch', draft: { editingDraft } });
			setFormErrors((prev) => ({ ...prev, ask: validateField('ask', askValue) }));
		},
		[actions, validateField],
	);
	const handleSubmit = useCallback(() => {
		void track('sap_step_submit', { step: normalizedStep, step_data: {}, step_data_raw: '{}' });

		submit('location');
	}, [normalizedStep, submit, track]);

	const handleBack = useCallback(() => {
		void track('sap_step_back', { step: 'create' });

		actions.send({ type: 'back' });
	}, [actions, track]);

	let imageButtonLabel;
	let imageButtonIcon: 'loader' | 'ai';

	if (status === 'pending') {
		imageButtonLabel = translationKey('corgi.start_a_petition.create.image_action_buttons.image_loading_button_label');
		imageButtonIcon = 'loader';
	} else if (imageGenerationCount > 0) {
		imageButtonLabel = translationKey('corgi.start_a_petition.create.image_action_buttons.make_new_image');
		imageButtonIcon = 'ai';
	} else {
		imageButtonLabel = translationKey('corgi.start_a_petition.create.image_action_buttons.make_image');
		imageButtonIcon = 'ai';
	}

	return {
		data: {
			ask: currentAsk,
			description: currentDescription,
			formRef,
			mode,
			errors: formErrors,
			jobStatus: status,
			imageSrc,
			imageRegenerationLeft: Math.max(0, MAX_IMAGE_GENERATION_COUNT - imageGenerationCount),
			imageRegenerationCount: imageGenerationCount,
			imageRegenerationDisabled,
			imageButtonLabel,
			imageButtonIcon,
		},
		actions: {
			back: handleBack,
			submit: handleSubmit,
			toOptionalPreview: () => submit('optional-preview'),
			resetFieldError,
			uploadImage,
			regenerateAiImage,
			persistDescription,
			persistAsk,
		},
	};
}
// oxlint-enable max-statements, complexity, max-lines-per-function
